//src/pages/admin/AdminSettings.jsx
import { useEffect, useState, useCallback } from "react";
import adminApi from "../../api/adminApi";
import {
  Save, RefreshCw, AlertTriangle, CheckCircle2,
  Info, TrendingUp, DollarSign, Percent, Eye, Zap, Shield, Banknote,
} from "lucide-react";
import { toast } from "react-toastify";
import FundingBonusSettings from "./FundingBonusSettings";

function MarginPreview({ margin }) {
  const examples = [100, 200, 500, 1000];
  return (
    <div className="mt-3 bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 flex items-center gap-1">
        <Eye size={11} />
        Price preview at {margin}x margin (example rate ₦1,550)
      </p>
      <div className="grid grid-cols-4 gap-2">
        {examples.map((usd) => (
          <div key={usd} className="text-center">
            <p className="text-xs text-gray-400 dark:text-gray-500">${usd}</p>
            <p className="text-xs font-semibold text-purple-600 dark:text-purple-400">
              ₦{Math.round(usd * 1550 * margin).toLocaleString()}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

function VaultAccMarginPreview({ margin }) {
  const examples = [500, 1000, 2000, 5000];
  return (
    <div className="mt-3 bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 flex items-center gap-1">
        <Eye size={11} />
        Price preview at {margin}x margin (VaultAcc base → your price)
      </p>
      <div className="grid grid-cols-4 gap-2">
        {examples.map((ngn) => (
          <div key={ngn} className="text-center">
            <p className="text-xs text-gray-400 dark:text-gray-500">₦{ngn.toLocaleString()}</p>
            <p className="text-xs font-semibold text-orange-600 dark:text-orange-400">
              ₦{Math.ceil(ngn * margin).toLocaleString()}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

function SameehaMarginPreview({ margin }) {
  const examples = [500, 1000, 2000, 5000];
  return (
    <div className="mt-3 bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 flex items-center gap-1">
        <Eye size={11} />
        Price preview at {margin}x margin (Sameeha base → your price)
      </p>
      <div className="grid grid-cols-4 gap-2">
        {examples.map((ngn) => (
          <div key={ngn} className="text-center">
            <p className="text-xs text-gray-400 dark:text-gray-500">₦{ngn.toLocaleString()}</p>
            <p className="text-xs font-semibold text-pink-600 dark:text-pink-400">
              ₦{Math.ceil(ngn * margin).toLocaleString()}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

function SujanMarginPreview({ margin }) {
  const examples = [700, 1200, 1800, 6000];
  return (
    <div className="mt-3 bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 flex items-center gap-1">
        <Eye size={11} />
        Price preview at {margin}x margin (Sujan base → your price)
      </p>
      <div className="grid grid-cols-4 gap-2">
        {examples.map((ngn) => (
          <div key={ngn} className="text-center">
            <p className="text-xs text-gray-400 dark:text-gray-500">₦{ngn.toLocaleString()}</p>
            <p className="text-xs font-semibold text-indigo-600 dark:text-indigo-400">
              ₦{Math.ceil(ngn * margin).toLocaleString()}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── The 6 Sujan margin categories ─────────────────────────────────────────────
const SUJAN_CATEGORIES = [
  {
    id:          "vpn1Month",
    label:       "All 1-Month VPNs",
    route:       "sujan-1month-margin",
    savingKey:   "vpn1Month",
    description: "Applies to: HMA VPN (1 Month), Express VPN 1 Month",
    products:    "HMA VPN · Express VPN 1 Month",
    badge:       "1-Month",
    badgeColor:  "bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300",
  },
  {
    id:          "vpn7Day",
    label:       "All 7-Day VPNs",
    route:       "sujan-7day-margin",
    savingKey:   "vpn7Day",
    description: "Applies to: Nord VPN, Nord VPN 7 Days, Pia VPN 7 Days",
    products:    "Nord VPN · Nord VPN 7 Days · Pia VPN 7 Days",
    badge:       "7-Day",
    badgeColor:  "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300",
  },
  {
    id:          "texting",
    label:       "TextPlus & NextPlus",
    route:       "sujan-texting-margin",
    savingKey:   "texting",
    description: "Applies to: TextPlus Google Login, NextPlus Google Login",
    products:    "TextPlus Google Login · NextPlus Google Login",
    badge:       "Texting Apps",
    badgeColor:  "bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300",
  },
  {
    id:          "googleVoice",
    label:       "Old & New Google Voice",
    route:       "sujan-googlevoice-margin",
    savingKey:   "googleVoice",
    description: "Applies to: Old Google Voice, New Google Voice (when available)",
    products:    "Old Google Voice · New Google Voice",
    badge:       "Google Voice",
    badgeColor:  "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300",
  },
  {
    id:          "expressLaptop",
    label:       "Express VPN For Laptop & PC",
    route:       "sujan-express-laptop-margin",
    savingKey:   "expressLaptop",
    description: "Applies to: Express VPN For Laptop & PC only (Sujan ID 23)",
    products:    "Express VPN For Laptop & PC",
    badge:       "Laptop VPN",
    badgeColor:  "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300",
  },
  {
    id:          "expressPhone",
    label:       "Express VPN For Phone",
    route:       "sujan-express-phone-margin",
    savingKey:   "expressPhone",
    description: "Applies to: Express VPN For Phone only (Sujan ID 22)",
    products:    "Express VPN For Phone",
    badge:       "Phone VPN",
    badgeColor:  "bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300",
  },
  {
    id:          "ipProxy",
    label:       "IP & Proxy",
    route:       "sujan-ip-proxy-margin",
    savingKey:   "ipProxy",
    description: "Applies to: all 9 PROXY listings — 5, 10, 20, 30, 40, 50 ip (Sujan IDs 60-65)",
    products:    "9 PROXY = 5/10/20/30/40/50 ip",
    badge:       "IP & Proxy",
    badgeColor:  "bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300",
  },
];

// ── Shopvia categories — a single hidden log source, but priced per-category
// since a Facebook account and a Hotmail email are worth very different amounts.
const SHOPVIA_CATEGORIES = [
  { id: "facebook",  label: "Facebook",  badgeColor: "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300" },
  { id: "instagram", label: "Instagram", badgeColor: "bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300" },
  { id: "tiktok",    label: "TikTok",    badgeColor: "bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-300" },
  { id: "twitter",   label: "Twitter/X", badgeColor: "bg-slate-100 dark:bg-slate-900/30 text-slate-700 dark:text-slate-300" },
  { id: "pinterest", label: "Pinterest", badgeColor: "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300" },
  { id: "hotmail",   label: "Hotmail",   badgeColor: "bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300" },
  { id: "outlook",   label: "Outlook",   badgeColor: "bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300" },
  { id: "email",     label: "Email",     badgeColor: "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300" },
  { id: "tools",     label: "Tools",     badgeColor: "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300" },
  { id: "other",     label: "Other",     badgeColor: "bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-300" },
];

export default function AdminSettings() {
  const [loading,         setLoading]         = useState(true);
  const [saving,          setSaving]           = useState(null);
  const [settings,        setSettings]         = useState(null);

  const [smsbowerMargin,       setSmsbowerMargin]       = useState("");
  const [daisyMargin,          setDaisyMargin]          = useState("");
  const [daisyCanadaMargin,    setDaisyCanadaMargin]    = useState("");
  const [getatextMargin,       setGetatextMargin]       = useState("");

  const [vaultaccMargin, setVaultaccMargin] = useState("");
  const [sameehaMargin,  setSameehaMargin]  = useState("");

  const [shopviaMargins, setShopviaMargins] = useState(
    Object.fromEntries(SHOPVIA_CATEGORIES.map((c) => [c.id, "1.2"]))
  );

  const [sujanMargins, setSujanMargins] = useState({
    vpn1Month:     "1.2",
    vpn7Day:       "1.2",
    texting:       "1.2",
    googleVoice:   "1.2",
    expressLaptop: "1.2",
    expressPhone:  "1.2",
    ipProxy:       "1.2",
  });

  const [rateOverride,  setRateOverride]  = useState("");
  const [useManualRate, setUseManualRate] = useState(false);

  const [manualFundAccount, setManualFundAccount] = useState({ bankName: "", accountNumber: "", accountName: "" });

  const validateMargin = (val) => {
    const n = Number(val);
    return !isNaN(n) && n >= 1.0 && n <= 10.0;
  };

  const validateSujanMargin = (val) => {
    const n = Number(val);
    return !isNaN(n) && n >= 1.0 && n <= 3.0;
  };

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.get("/settings");
      if (res.data.success) {
        const s = res.data.settings;
        setSettings(s);

        setSmsbowerMargin(String(s.providerMargins?.smsbower        || 1.2));
        setDaisyMargin(String(s.providerMargins?.daisysms           || 1.2));
        setDaisyCanadaMargin(String(s.providerMargins?.daisysms_canada || 1.2));
        setGetatextMargin(String(s.providerMargins?.getatext        || 1.2));
        setVaultaccMargin(String(s.vaultaccMargin || 1.2));
        setSameehaMargin(String(s.sameehaMargin || 1.2));

        setShopviaMargins(
          Object.fromEntries(
            SHOPVIA_CATEGORIES.map((c) => [c.id, String(s.shopviaMargins?.[c.id] || 1.2)])
          )
        );

        setSujanMargins({
          vpn1Month:     String(s.sujanMargins?.vpn1Month     || 1.2),
          vpn7Day:       String(s.sujanMargins?.vpn7Day       || 1.2),
          texting:       String(s.sujanMargins?.texting       || 1.2),
          googleVoice:   String(s.sujanMargins?.googleVoice   || 1.2),
          expressLaptop: String(s.sujanMargins?.expressLaptop || 1.2),
          expressPhone:  String(s.sujanMargins?.expressPhone  || 1.2),
          ipProxy:       String(s.sujanMargins?.ipProxy       || 1.2),
        });

        const savedRate = s.usdToNgnRate || 0;
        setManualFundAccount(s.manualFundingAccount || { bankName: "", accountNumber: "", accountName: "" });
        setRateOverride(savedRate > 0 ? String(savedRate) : "");
        setUseManualRate(savedRate > 0);
        setUseManualRate(savedRate > 0);
      }
    } catch {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const saveProviderMargins = async () => {
    if (!validateMargin(smsbowerMargin))    { toast.error("SMSBower margin must be 1.0–10.0"); return; }
    if (!validateMargin(daisyMargin))       { toast.error("DaisySMS — USA margin must be 1.0–10.0"); return; }
    if (!validateMargin(daisyCanadaMargin)) { toast.error("DaisySMS — Canada margin must be 1.0–10.0"); return; }
    if (!validateMargin(getatextMargin))    { toast.error("Getatext (Alternative) margin must be 1.0–10.0"); return; }
    setSaving("margins");
    try {
      await adminApi.put("/settings/provider-margins", {
        smsbower:        Number(smsbowerMargin),
        daisysms:        Number(daisyMargin),
        daisysms_canada: Number(daisyCanadaMargin),
        getatext:        Number(getatextMargin),
      });
      toast.success("Provider margins saved");
      fetchSettings();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update margins");
    } finally {
      setSaving(null);
    }
  };

  const saveVaultaccMargin = async () => {
    if (!validateMargin(vaultaccMargin)) { toast.error("VaultAcc margin must be 1.0–10.0"); return; }
    setSaving("vaultacc");
    try {
      await adminApi.put("/settings/vaultacc-margin", { margin: Number(vaultaccMargin) });
      toast.success(`VaultAcc margin set to ${vaultaccMargin}x (${Math.round((Number(vaultaccMargin) - 1) * 100)}% markup)`);
      fetchSettings();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update VaultAcc margin");
    } finally {
      setSaving(null);
    }
  };

  const saveSameehaMargin = async () => {
    if (!validateMargin(sameehaMargin)) { toast.error("Sameeha margin must be 1.0–10.0"); return; }
    setSaving("sameeha");
    try {
      await adminApi.put("/settings/sameeha-margin", { margin: Number(sameehaMargin) });
      toast.success(`Sameeha margin set to ${sameehaMargin}x (${Math.round((Number(sameehaMargin) - 1) * 100)}% markup)`);
      fetchSettings();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update Sameeha margin");
    } finally {
      setSaving(null);
    }
  };

  const saveShopviaMargins = async () => {
    const invalid = SHOPVIA_CATEGORIES.find((c) => !validateMargin(shopviaMargins[c.id]));
    if (invalid) {
      toast.error(`${invalid.label} margin must be 1.0–10.0`);
      return;
    }
    setSaving("shopvia");
    try {
      const body = Object.fromEntries(
        SHOPVIA_CATEGORIES.map((c) => [c.id, Number(shopviaMargins[c.id])])
      );
      await adminApi.put("/settings/shopvia-margins", body);
      toast.success("Shopvia margins saved");
      fetchSettings();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update Shopvia margins");
    } finally {
      setSaving(null);
    }
  };

  const saveSujanCategoryMargin = async (cat) => {
    const val = sujanMargins[cat.id];
    if (!validateSujanMargin(val)) {
      toast.error(`${cat.label} margin must be 1.0–3.0 (max 200% markup)`);
      return;
    }
    setSaving(cat.id);
    try {
      await adminApi.put(`/settings/${cat.route}`, { margin: Number(val) });
      toast.success(`${cat.label} set to ${val}x (${Math.round((Number(val) - 1) * 100)}% markup)`);
      fetchSettings();
    } catch (err) {
      toast.error(err.response?.data?.message || `Failed to update ${cat.label}`);
    } finally {
      setSaving(null);
    }
  };



    const saveManualFundAccount = async () => {
    if (!manualFundAccount.bankName || !manualFundAccount.accountNumber || !manualFundAccount.accountName) {
      toast.error("All three fields are required");
      return;
    }
    setSaving("manualFund");
    try {
      await adminApi.put("/settings/manual-funding-account", manualFundAccount);
      toast.success("Manual funding account updated");
      fetchSettings();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update");
    } finally {
      setSaving(null);
    }
  };


  const saveRate = async () => {
    if (useManualRate) {
      const n = Number(rateOverride);
      if (isNaN(n) || n < 500 || n > 5000) { toast.error("Rate must be between 500 and 5000"); return; }
    }
    setSaving("rate");
    try {
      await adminApi.put("/settings/rate", { rate: useManualRate ? Number(rateOverride) : 0 });
      toast.success(useManualRate ? `Rate locked to ₦${Number(rateOverride).toLocaleString()}` : "Reverted to live Wise rate");
      fetchSettings();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update rate");
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw size={24} className="text-purple-500 animate-spin" />
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Platform Settings</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            Control pricing margins and exchange rate
          </p>
        </div>
        <button onClick={fetchSettings}
          className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-purple-400 transition">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Info banner */}
      <div className="flex items-start gap-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
        <Info size={16} className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-blue-600 dark:text-blue-400 leading-relaxed">
          For number providers: Raw USD price × live NGN rate × margin = price user pays.
          For VaultAcc logs: VaultAcc base price (₦) × VaultAcc margin = price user pays.
          For Sujan VPN/Texting/Google Voice: Sujan base price (₦) × category margin = price user pays. Max markup is 200% (3.0x).
        </p>
      </div>

      {/* ── Sujan VPN, Texting & Google Voice Margins ─────────────────────────── */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-indigo-200 dark:border-indigo-900 overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-indigo-100 dark:border-indigo-900">
          <div className="w-8 h-8 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 flex items-center justify-center">
            <Shield size={15} className="text-indigo-600 dark:text-indigo-400" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
              VPN, Texting & Google Voice Margins
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              6 categories — each with its own margin. Enter any value from 1.0 to 3.0 (up to 200% markup).
            </p>
          </div>
          <span className="ml-auto px-2 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 text-xs rounded-full font-medium whitespace-nowrap">
            Sujan Integration
          </span>
        </div>

        <div className="p-5 space-y-0">

          <div className="flex items-start gap-3 p-3 mb-5 bg-indigo-50 dark:bg-indigo-900/10 border border-indigo-200 dark:border-indigo-800 rounded-lg">
            <Info size={13} className="text-indigo-600 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-indigo-700 dark:text-indigo-400 leading-relaxed space-y-1">
              <p>Sujan prices are in Naira. Each category below has its own margin applied on top.</p>
              <p>
                <strong>Express VPN For Laptop & PC</strong> and <strong>Express VPN For Phone</strong> now have separate margins.<br />
                <strong>TextPlus & NextPlus</strong> = texting apps (separate from Google Voice).<br />
                <strong>Google Voice</strong> = Old Google Voice + New Google Voice (when available).<br />
                Maximum markup is <strong>200% (3.0x)</strong>. You can type any decimal e.g. 2.5, 1.75, 3.0.
              </p>
            </div>
          </div>

          {SUJAN_CATEGORIES.map((cat, idx) => {
            const val        = sujanMargins[cat.id] || "1.2";
            const numVal     = Number(val) || 1.2;
            const isValid    = validateSujanMargin(val);
            const markupPct  = ((numVal - 1) * 100).toFixed(0);
            const savedVal   = settings?.sujanMargins?.[cat.savingKey] || 1.2;
            const isSaving   = saving === cat.id;
            const isLastItem = idx === SUJAN_CATEGORIES.length - 1;

            return (
              <div key={cat.id}>
                <div className="py-5">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">
                      {cat.label}
                    </span>
                    <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${cat.badgeColor}`}>
                      {cat.badge}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mb-3">
                    {cat.products}
                  </p>

                  <div className="flex items-center gap-3">
                    <div className="flex flex-col gap-1">
                      <input
                        type="number"
                        min="1.0"
                        max="3.0"
                        step="0.05"
                        value={val}
                        onChange={(e) =>
                          setSujanMargins(prev => ({ ...prev, [cat.id]: e.target.value }))
                        }
                        className="w-36 px-4 py-2.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:text-white font-mono"
                      />
                      <p className="text-xs text-gray-400 dark:text-gray-500 pl-1">Range: 1.0 – 3.0</p>
                    </div>
                    <div className="text-xs text-gray-400 space-y-0.5">
                      <p>Saved: <span className="font-semibold text-gray-700 dark:text-gray-300">{savedVal}x</span></p>
                      <p>Markup: <span className="font-semibold text-indigo-600 dark:text-indigo-400">{markupPct}%</span></p>
                    </div>
                  </div>

                  <div className="flex gap-2 mt-2">
                    {["1.0", "1.5", "2.0", "2.5", "3.0"].map(preset => (
                      <button
                        key={preset}
                        onClick={() => setSujanMargins(prev => ({ ...prev, [cat.id]: preset }))}
                        className={`px-2.5 py-1 text-xs rounded-lg border transition font-mono ${
                          val === preset
                            ? "bg-indigo-600 text-white border-indigo-600"
                            : "bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:border-indigo-400"
                        }`}
                      >
                        {preset}x
                      </button>
                    ))}
                  </div>

                  {val && isValid  && <SujanMarginPreview margin={numVal} />}
                  {val && !isValid && (
                    <p className="flex items-center gap-1 text-xs text-red-500 mt-1.5">
                      <AlertTriangle size={11} /> Must be between 1.0 and 3.0 (max 200% markup)
                    </p>
                  )}

                  <button
                    onClick={() => saveSujanCategoryMargin(cat)}
                    disabled={isSaving || !isValid}
                    className="mt-4 flex items-center gap-2 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white text-sm font-medium rounded-lg transition"
                  >
                    {isSaving
                      ? <><RefreshCw size={14} className="animate-spin" />Saving...</>
                      : <><Save size={14} />Save {cat.label}</>
                    }
                  </button>
                </div>

                {!isLastItem && (
                  <div className="border-t border-gray-100 dark:border-gray-800" />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── VaultAcc Margin ───────────────────────────────────────────────────── */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-orange-200 dark:border-orange-900 overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-orange-100 dark:border-orange-900">
          <div className="w-8 h-8 rounded-lg bg-orange-50 dark:bg-orange-900/20 flex items-center justify-center">
            <Zap size={15} className="text-orange-600 dark:text-orange-400" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">VaultAcc Logs Margin</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">Controls markup on logs fetched from VaultAcc</p>
          </div>
          <span className="ml-auto px-2 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 text-xs rounded-full font-medium">
            VaultAcc Integration
          </span>
        </div>
        <div className="p-5">
          <div className="flex items-start gap-3 mb-4 p-3 bg-orange-50 dark:bg-orange-900/10 border border-orange-200 dark:border-orange-800 rounded-lg">
            <Info size={13} className="text-orange-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-orange-700 dark:text-orange-400 leading-relaxed">
              VaultAcc prices are in Naira. This margin is applied directly on top.
              Example: VaultAcc charges ₦1,500 × 1.2 margin = you charge ₦1,800.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <input
              type="number" min="1.0" max="10.0" step="0.05"
              value={vaultaccMargin}
              onChange={(e) => setVaultaccMargin(e.target.value)}
              className="w-36 px-4 py-2.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 dark:text-white font-mono"
            />
            <div className="text-xs text-gray-400 space-y-0.5">
              <p>Saved: <span className="font-semibold text-gray-700 dark:text-gray-300">{settings?.vaultaccMargin || 1.2}x</span></p>
              <p>Markup: <span className="font-semibold text-orange-600 dark:text-orange-400">
                {((Number(vaultaccMargin || 1.2) - 1) * 100).toFixed(0)}%
              </span></p>
            </div>
          </div>
          {vaultaccMargin && validateMargin(vaultaccMargin) && <VaultAccMarginPreview margin={Number(vaultaccMargin)} />}
          {vaultaccMargin && !validateMargin(vaultaccMargin) && (
            <p className="flex items-center gap-1 text-xs text-red-500 mt-1.5">
              <AlertTriangle size={11} /> Must be 1.0–10.0
            </p>
          )}
          <button onClick={saveVaultaccMargin} disabled={saving === "vaultacc"}
            className="mt-4 flex items-center gap-2 px-5 py-2.5 bg-orange-600 hover:bg-orange-700 disabled:bg-orange-400 text-white text-sm font-medium rounded-lg transition">
            {saving === "vaultacc"
              ? <><RefreshCw size={14} className="animate-spin" />Saving...</>
              : <><Save size={14} />Save VaultAcc Margin</>}
          </button>
        </div>
      </div>

      {/* ── Sameeha TikTok Margin ─────────────────────────────────────────────── */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-pink-200 dark:border-pink-900 overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-pink-100 dark:border-pink-900">
          <div className="w-8 h-8 rounded-lg bg-pink-50 dark:bg-pink-900/20 flex items-center justify-center">
            <Zap size={15} className="text-pink-600 dark:text-pink-400" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Sameeha TikTok Margin</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">Controls markup on TikTok logs fetched from Sameeha Socialhub</p>
          </div>
          <span className="ml-auto px-2 py-1 bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300 text-xs rounded-full font-medium">
            Sameeha Integration
          </span>
        </div>
        <div className="p-5">
          <div className="flex items-start gap-3 mb-4 p-3 bg-pink-50 dark:bg-pink-900/10 border border-pink-200 dark:border-pink-800 rounded-lg">
            <Info size={13} className="text-pink-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-pink-700 dark:text-pink-400 leading-relaxed">
              Sameeha prices are in Naira. This margin is applied directly on top, same as VaultAcc.
              Example: Sameeha charges ₦2,000 × 1.2 margin = you charge ₦2,400.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <input
              type="number" min="1.0" max="10.0" step="0.05"
              value={sameehaMargin}
              onChange={(e) => setSameehaMargin(e.target.value)}
              className="w-36 px-4 py-2.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 dark:text-white font-mono"
            />
            <div className="text-xs text-gray-400 space-y-0.5">
              <p>Saved: <span className="font-semibold text-gray-700 dark:text-gray-300">{settings?.sameehaMargin || 1.2}x</span></p>
              <p>Markup: <span className="font-semibold text-pink-600 dark:text-pink-400">
                {((Number(sameehaMargin || 1.2) - 1) * 100).toFixed(0)}%
              </span></p>
            </div>
          </div>
          {sameehaMargin && validateMargin(sameehaMargin) && <SameehaMarginPreview margin={Number(sameehaMargin)} />}
          {sameehaMargin && !validateMargin(sameehaMargin) && (
            <p className="flex items-center gap-1 text-xs text-red-500 mt-1.5">
              <AlertTriangle size={11} /> Must be 1.0–10.0
            </p>
          )}
          <button onClick={saveSameehaMargin} disabled={saving === "sameeha"}
            className="mt-4 flex items-center gap-2 px-5 py-2.5 bg-pink-600 hover:bg-pink-700 disabled:bg-pink-400 text-white text-sm font-medium rounded-lg transition">
            {saving === "sameeha"
              ? <><RefreshCw size={14} className="animate-spin" />Saving...</>
              : <><Save size={14} />Save Sameeha Margin</>}
          </button>
        </div>
      </div>

      {/* ── Shopvia Margins — per category, saved together ───────────────────── */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-teal-200 dark:border-teal-900 overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-teal-100 dark:border-teal-900">
          <div className="w-8 h-8 rounded-lg bg-teal-50 dark:bg-teal-900/20 flex items-center justify-center">
            <Zap size={15} className="text-teal-600 dark:text-teal-400" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Shopvia Log Margins</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">One margin per category — priced in USD × live rate, same as other number providers</p>
          </div>
          <span className="ml-auto px-2 py-1 bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 text-xs rounded-full font-medium whitespace-nowrap">
            Shopvia Integration
          </span>
        </div>

        <div className="p-5">
          <div className="flex items-start gap-3 mb-4 p-3 bg-teal-50 dark:bg-teal-900/10 border border-teal-200 dark:border-teal-800 rounded-lg">
            <Info size={13} className="text-teal-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-teal-700 dark:text-teal-400 leading-relaxed">
              Every category below shares the same underlying source, priced independently —
              set each one to whatever reflects its real value. Base price (USD) × live rate × margin = price shown to users.
            </p>
          </div>

          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {SHOPVIA_CATEGORIES.map((cat) => {
              const val     = shopviaMargins[cat.id] || "1.2";
              const numVal  = Number(val) || 1.2;
              const isValid = validateMargin(val);

              return (
                <div key={cat.id} className="py-4 first:pt-0 last:pb-0">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">{cat.label}</span>
                    <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${cat.badgeColor}`}>
                      {cat.label}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <input
                      type="number" min="1.0" max="10.0" step="0.05"
                      value={val}
                      onChange={(e) => setShopviaMargins((prev) => ({ ...prev, [cat.id]: e.target.value }))}
                      className="w-36 px-4 py-2.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 dark:text-white font-mono"
                    />
                    <div className="text-xs text-gray-400 space-y-0.5">
                      <p>Saved: <span className="font-semibold text-gray-700 dark:text-gray-300">{settings?.shopviaMargins?.[cat.id] || 1.2}x</span></p>
                      <p>Markup: <span className="font-semibold text-teal-600 dark:text-teal-400">{((numVal - 1) * 100).toFixed(0)}%</span></p>
                    </div>
                  </div>
                  {val && isValid && <MarginPreview margin={numVal} />}
                  {val && !isValid && (
                    <p className="flex items-center gap-1 text-xs text-red-500 mt-1.5">
                      <AlertTriangle size={11} /> Must be 1.0–10.0
                    </p>
                  )}
                </div>
              );
            })}
          </div>

          <button onClick={saveShopviaMargins} disabled={saving === "shopvia"}
            className="mt-5 flex items-center gap-2 px-5 py-2.5 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white text-sm font-medium rounded-lg transition">
            {saving === "shopvia"
              ? <><RefreshCw size={14} className="animate-spin" />Saving...</>
              : <><Save size={14} />Save All Shopvia Margins</>}
          </button>
        </div>
      </div>

      {/* ── Provider Margins ──────────────────────────────────────────────────── */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 dark:border-gray-800">
          <div className="w-8 h-8 rounded-lg bg-purple-50 dark:bg-purple-900/20 flex items-center justify-center">
            <Percent size={15} className="text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Provider Margins</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">1.0 = no markup · 1.3 = 30% markup</p>
          </div>
        </div>
        <div className="p-5 space-y-6">
          {/* SMSBower */}
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm font-semibold text-gray-900 dark:text-white">SMSBower</span>
              <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs rounded-full">All Countries page</span>
            </div>
            <div className="flex items-center gap-3 mt-2">
              <input type="number" min="1.0" max="10.0" step="0.05" value={smsbowerMargin}
                onChange={(e) => setSmsbowerMargin(e.target.value)}
                className="w-36 px-4 py-2.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:text-white font-mono" />
              <div className="text-xs text-gray-400 space-y-0.5">
                <p>Saved: <span className="font-semibold text-gray-700 dark:text-gray-300">{settings?.providerMargins?.smsbower || 1.2}x</span></p>
                <p>Markup: <span className="font-semibold text-purple-600 dark:text-purple-400">{((Number(smsbowerMargin || 1.2) - 1) * 100).toFixed(0)}%</span></p>
              </div>
            </div>
            {smsbowerMargin && validateMargin(smsbowerMargin) && <MarginPreview margin={Number(smsbowerMargin)} />}
            {smsbowerMargin && !validateMargin(smsbowerMargin) && (
              <p className="flex items-center gap-1 text-xs text-red-500 mt-1.5"><AlertTriangle size={11} /> Must be 1.0–10.0</p>
            )}
          </div>
          <div className="border-t border-gray-100 dark:border-gray-800" />
          {/* DaisySMS — Standard */}
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm font-semibold text-gray-900 dark:text-white">DaisySMS</span>
              <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 text-xs rounded-full">USA Numbers — Standard</span>
            </div>
            <div className="flex items-center gap-3 mt-2">
              <input type="number" min="1.0" max="10.0" step="0.05" value={daisyMargin}
                onChange={(e) => setDaisyMargin(e.target.value)}
                className="w-36 px-4 py-2.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:text-white font-mono" />
              <div className="text-xs text-gray-400 space-y-0.5">
                <p>Saved: <span className="font-semibold text-gray-700 dark:text-gray-300">{settings?.providerMargins?.daisysms || 1.2}x</span></p>
                <p>Markup: <span className="font-semibold text-purple-600 dark:text-purple-400">{((Number(daisyMargin || 1.2) - 1) * 100).toFixed(0)}%</span></p>
              </div>
            </div>
            {daisyMargin && validateMargin(daisyMargin) && <MarginPreview margin={Number(daisyMargin)} />}
            {daisyMargin && !validateMargin(daisyMargin) && (
              <p className="flex items-center gap-1 text-xs text-red-500 mt-1.5"><AlertTriangle size={11} /> Must be 1.0–10.0</p>
            )}
          </div>
          <div className="border-t border-gray-100 dark:border-gray-800" />
          {/* DaisySMS — Canada */}
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm font-semibold text-gray-900 dark:text-white">DaisySMS — Canada</span>
              <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs rounded-full">"_canada" services</span>
            </div>
            <div className="flex items-center gap-3 mt-2">
              <input type="number" min="1.0" max="10.0" step="0.05" value={daisyCanadaMargin}
                onChange={(e) => setDaisyCanadaMargin(e.target.value)}
                className="w-36 px-4 py-2.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:text-white font-mono" />
              <div className="text-xs text-gray-400 space-y-0.5">
                <p>Saved: <span className="font-semibold text-gray-700 dark:text-gray-300">{settings?.providerMargins?.daisysms_canada || 1.2}x</span></p>
                <p>Markup: <span className="font-semibold text-purple-600 dark:text-purple-400">{((Number(daisyCanadaMargin || 1.2) - 1) * 100).toFixed(0)}%</span></p>
              </div>
            </div>
            {daisyCanadaMargin && validateMargin(daisyCanadaMargin) && <MarginPreview margin={Number(daisyCanadaMargin)} />}
            {daisyCanadaMargin && !validateMargin(daisyCanadaMargin) && (
              <p className="flex items-center gap-1 text-xs text-red-500 mt-1.5"><AlertTriangle size={11} /> Must be 1.0–10.0</p>
            )}
          </div>
          <div className="border-t border-gray-100 dark:border-gray-800" />
          {/* Getatext — Alternative */}
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm font-semibold text-gray-900 dark:text-white">Getatext</span>
              <span className="px-2 py-0.5 bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400 text-xs rounded-full">USA Numbers — Alternative</span>
            </div>
            <div className="flex items-center gap-3">
              <input type="number" min="1.0" max="10.0" step="0.05" value={getatextMargin}
                onChange={(e) => setGetatextMargin(e.target.value)}
                className="w-36 px-4 py-2.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:text-white font-mono" />
              <div className="text-xs text-gray-400 space-y-0.5">
                <p>Saved: <span className="font-semibold text-gray-700 dark:text-gray-300">{settings?.providerMargins?.getatext || 1.2}x</span></p>
                <p>Markup: <span className="font-semibold text-purple-600 dark:text-purple-400">{((Number(getatextMargin || 1.2) - 1) * 100).toFixed(0)}%</span></p>
              </div>
            </div>
            {getatextMargin && validateMargin(getatextMargin) && <MarginPreview margin={Number(getatextMargin)} />}
            {getatextMargin && !validateMargin(getatextMargin) && (
              <p className="flex items-center gap-1 text-xs text-red-500 mt-1.5"><AlertTriangle size={11} /> Must be 1.0–10.0</p>
            )}
          </div>
          <button onClick={saveProviderMargins} disabled={saving === "margins"}
            className="flex items-center gap-2 px-5 py-2.5 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white text-sm font-medium rounded-lg transition">
            {saving === "margins"
              ? <><RefreshCw size={14} className="animate-spin" />Saving...</>
              : <><Save size={14} />Save Provider Margins</>}
          </button>
        </div>
      </div>



            {/* ── Manual Funding Account ────────────────────────────────────────────── */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-green-200 dark:border-green-900 overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-green-100 dark:border-green-900">
          <div className="w-8 h-8 rounded-lg bg-green-50 dark:bg-green-900/20 flex items-center justify-center">
            <Banknote size={15} className="text-green-600 dark:text-green-400" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Manual Funding Account</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">Shown to users who choose "Fund Manually" instead of auto-generating a virtual account</p>
          </div>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Bank Name</label>
            <input type="text" value={manualFundAccount.bankName}
              onChange={(e) => setManualFundAccount({ ...manualFundAccount, bankName: e.target.value })}
              placeholder="e.g. Moniepoint MFB"
              className="w-full px-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 dark:text-white" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Account Number</label>
            <input type="text" value={manualFundAccount.accountNumber}
              onChange={(e) => setManualFundAccount({ ...manualFundAccount, accountNumber: e.target.value })}
              placeholder="e.g. 6419030972"
              className="w-full px-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 dark:text-white font-mono" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Account Name</label>
            <input type="text" value={manualFundAccount.accountName}
              onChange={(e) => setManualFundAccount({ ...manualFundAccount, accountName: e.target.value })}
              placeholder="e.g. Lio Global Vnetures Ltd"
              className="w-full px-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 dark:text-white" />
          </div>
          <button onClick={saveManualFundAccount} disabled={saving === "manualFund"}
            className="flex items-center gap-2 px-5 py-2.5 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white text-sm font-medium rounded-lg transition">
            {saving === "manualFund"
              ? <><RefreshCw size={14} className="animate-spin" />Saving...</>
              : <><Save size={14} />Save Manual Funding Account</>}
          </button>
        </div>
      </div>




      {/* ── USD → NGN Rate ────────────────────────────────────────────────────── */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 dark:border-gray-800">
          <div className="w-8 h-8 rounded-lg bg-green-50 dark:bg-green-900/20 flex items-center justify-center">
            <DollarSign size={15} className="text-green-600 dark:text-green-400" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">USD → NGN Exchange Rate</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">Live from Wise.com by default.</p>
          </div>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-xl">
            <TrendingUp size={16} className="text-green-600 dark:text-green-400 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-xs font-medium text-gray-700 dark:text-gray-300">
                {settings?.usdToNgnRate > 0
                  ? `Manual rate: ₦${Number(settings.usdToNgnRate).toLocaleString()} per $1`
                  : "Live rate active (Wise.com, refreshed every 15 min)"}
              </p>
            </div>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              settings?.usdToNgnRate > 0
                ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
                : "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
            }`}>
              {settings?.usdToNgnRate > 0 ? "Manual" : "Live"}
            </span>
          </div>
          <div className="flex gap-3">
            <button onClick={() => setUseManualRate(false)}
              className={`flex-1 py-2 text-xs font-medium rounded-lg transition ${
                !useManualRate ? "bg-green-600 text-white" : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400"
              }`}>
              Use Live Rate
            </button>
            <button onClick={() => setUseManualRate(true)}
              className={`flex-1 py-2 text-xs font-medium rounded-lg transition ${
                useManualRate ? "bg-amber-600 text-white" : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400"
              }`}>
              Set Fixed Rate
            </button>
          </div>
          {useManualRate && (
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                Fixed Rate (₦ per $1)
              </label>
              <div className="flex items-center gap-3">
                <div className="relative flex-1 max-w-xs">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium">₦</span>
                  <input type="number" min="500" max="5000" step="10" value={rateOverride}
                    onChange={(e) => setRateOverride(e.target.value)}
                    className="w-full pl-8 pr-4 py-2.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:text-white font-mono"
                    placeholder="1580" />
                </div>
                <p className="text-xs text-gray-400">500–5000</p>
              </div>
            </div>
          )}
          {!useManualRate && (
            <div className="flex items-start gap-2 bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800 rounded-lg p-3">
              <CheckCircle2 size={13} className="text-green-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-green-700 dark:text-green-400">
                Live rate fetched from Wise.com, cached 15 min.
              </p>
            </div>
          )}
          <button onClick={saveRate} disabled={saving === "rate"}
            className="flex items-center gap-2 px-5 py-2.5 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white text-sm font-medium rounded-lg transition">
            {saving === "rate"
              ? <><RefreshCw size={14} className="animate-spin" />Saving...</>
              : <><Save size={14} />Save Rate Setting</>}
          </button>
        </div>
      </div>

      <FundingBonusSettings />

      {settings?.updatedAt?.providerMargins && (
        <p className="text-xs text-gray-400 text-center pb-4">
          Last updated: {new Date(settings.updatedAt.providerMargins).toLocaleString("en-NG")}
        </p>
      )}
    </div>
  );
}